import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  widgetName?: string;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
}

/**
 * WidgetErrorBoundary
 *
 * Per-widget error boundary. Unlike the app-root ErrorBoundary (which shows a
 * full-screen fallback and blanks the whole app), this contains a render-time
 * crash to the single widget that threw, so one bad cell renderer can't take
 * down the entire layout. The "Reload" button resets the boundary and re-mounts
 * the widget — useful when the crash was a transient data race that has since
 * resolved (store hydrated after first render).
 */
class WidgetErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `WidgetErrorBoundary caught in ${this.props.widgetName ?? "widget"}:`,
      error,
      info.componentStack,
    );
    this.props.onError?.(error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="WidgetErrorBoundaryContainer"
        id="WidgetErrorBoundaryContainer"
        className="flex h-full w-full flex-col items-center justify-center gap-3 p-4 text-center"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          An error has occurred. Please click &lsquo;Reload&rsquo;
        </p>
        <button
          onClick={this.handleReload}
          data-testid="WidgetErrorBoundaryReloadButton"
          id="WidgetErrorBoundaryReloadButton"
          className="cursor-pointer rounded-md border border-gray-400 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:border-zinc-600 dark:text-gray-300 dark:hover:bg-zinc-800"
        >
          Reload
        </button>
      </div>
    );
  }
}

export default WidgetErrorBoundary;
